import type { CefrLevel } from '@/features/nectar/api/models'
import { useProficiencyAssessment } from '@/features/nectar/hooks/useProficiencyAssessment'
import { useTaskHeartbeat } from '@/features/nectar/hooks/useTaskHeartbeat'
import { useProfile } from '@/features/profile/api/profile'
import { useTurnstileCaptcha } from '@/features/turnstile'
import { Button, Spinner, Text } from '@/ui-kit'
import { Trans } from '@lingui/react/macro'
import { useCallback, useMemo } from 'react'
import { isProlificMode } from '../../prolific/session'
import type { ProjectFlowProps } from '../project-flows'
import { AssessmentCountdown } from './AssessmentCountdown'
import { AssessmentStepper } from './AssessmentStepper'
import { PhaseClaimReward } from './PhaseClaimReward'
import { PhaseResults } from './PhaseResults'
import { PhaseSetup } from './PhaseSetup'
import { PhaseSpeechRecording } from './PhaseSpeechRecording'
import { PhaseTextQuestion } from './PhaseTextQuestion'

const CEFR_LEVEL_SET = new Set<CefrLevel>(['A1', 'A2', 'B1', 'B2', 'C1', 'C2'])

const ISO1_TO_PROFILE_CODE: Record<string, string> = {
  en: 'eng'
}

function resolveDefaultLevel(profileLevel: string | undefined): CefrLevel | undefined {
  if (!profileLevel) return undefined
  if (profileLevel === 'Native') return 'C2'
  return CEFR_LEVEL_SET.has(profileLevel as CefrLevel) ? (profileLevel as CefrLevel) : undefined
}

type ProficiencyAssessmentFlowProps = Omit<ProjectFlowProps, 'projectId'> & {
  projectId?: string
}

const ProficiencyAssessmentFlow = ({
  projectId,
  language,
  captchaEnabled,
  onBack,
  onCompleted
}: ProficiencyAssessmentFlowProps) => {
  const submissionCaptcha = useTurnstileCaptcha({ enabled: captchaEnabled, action: 'submission' })
  const { data: profile } = useProfile()
  const prolificMode = isProlificMode()

  const defaultLevel = useMemo(() => {
    const profileCode = ISO1_TO_PROFILE_CODE[language]
    if (!profileCode) return undefined
    return resolveDefaultLevel(profile?.languages?.find((l) => l.languageCode === profileCode)?.level)
  }, [profile, language])

  const getSubmissionToken = useCallback(() => submissionCaptcha.tokenRef.current, [submissionCaptcha.tokenRef])
  const resetSubmissionWidget = useCallback(() => submissionCaptcha.reset(), [submissionCaptcha])

  const {
    phase,
    assessmentId,
    taskId,
    expiresAt,
    finalResult,
    starting,
    claiming,
    progress,
    setProgress,
    startAssessment,
    claimReward,
    handleTextCompleted,
    handleAllRecorded,
    handleExpired
  } = useProficiencyAssessment({
    projectId,
    language,
    captchaEnabled,
    getSubmissionToken,
    resetSubmissionWidget,
    onBack,
    onCompleted
  })

  useTaskHeartbeat({
    projectId,
    taskId,
    enabled: phase === 'text-phase' || phase === 'speech-phase'
  })

  const showHeader = phase === 'text-phase' || phase === 'speech-phase' || phase === 'completing'

  const showSubmissionCaptcha = phase === 'completing'

  return (
    <div className="flex flex-col" style={{ animation: 'nectar-slide-up 0.4s ease-out' }}>
      {showHeader && (
        <div className="mx-auto flex w-full max-w-[600px] flex-col gap-3 pb-4">
          <div className="flex items-center justify-between gap-2">
            <Text type="body3-regular" className="text-gray-60 tracking-wide uppercase">
              <Trans>Proficiency Assessment</Trans>
            </Text>
            {expiresAt && <AssessmentCountdown expiresAt={expiresAt} onExpired={handleExpired} />}
          </div>
          {progress && <AssessmentStepper progress={progress} />}
        </div>
      )}

      {phase === 'loading' && (
        <div className="flex w-full justify-center py-12">
          <Spinner />
        </div>
      )}

      {phase === 'setup' && (
        <PhaseSetup
          key={defaultLevel ?? 'default'}
          onStart={startAssessment}
          starting={starting}
          defaultLevel={defaultLevel}
        />
      )}

      {phase === 'claim-reward' && finalResult && (
        <PhaseClaimReward
          finalResult={finalResult}
          claiming={claiming}
          captchaEnabled={captchaEnabled}
          onClaim={claimReward}
        />
      )}

      {phase === 'text-phase' && assessmentId && (
        <PhaseTextQuestion
          key={assessmentId}
          assessmentId={assessmentId}
          onTextCompleted={handleTextCompleted}
          onProgress={setProgress}
        />
      )}

      {phase === 'speech-phase' && assessmentId && (
        <PhaseSpeechRecording
          key={`speech-${assessmentId}`}
          assessmentId={assessmentId}
          onAllRecorded={handleAllRecorded}
          onProgress={setProgress}
        />
      )}

      {phase === 'completing' && (
        <div className="flex w-full justify-center py-12">
          <div className="flex flex-col items-center gap-3">
            <Spinner />
            <Text type="body2-regular" className="text-gray-80">
              <Trans>Evaluating your responses...</Trans>
            </Text>
          </div>
        </div>
      )}

      {showSubmissionCaptcha && (
        <div className="max-mobile:max-w-[320px] mx-auto mt-4 flex w-full max-w-[400px] flex-col items-center gap-2">
          {submissionCaptcha.renderWidget('w-full')}
        </div>
      )}

      {phase === 'results' && finalResult && (
        <PhaseResults finalResult={finalResult} onBack={onBack} hideBackAction={prolificMode} />
      )}

      {phase === 'results' && !finalResult && (
        <div
          className="mx-auto flex w-full max-w-[600px] flex-col items-center gap-4 rounded-2xl border border-solid border-gray-100 px-10 py-12 text-center"
          style={{ animation: 'nectar-slide-up 0.4s ease-out' }}
        >
          <Text type="h5" className="text-gray-70">
            <Trans>Assessment Completed</Trans>
          </Text>
          <Text type="body2-regular" className="text-gray-80">
            <Trans>You have already completed this assessment.</Trans>
          </Text>
          {!prolificMode && (
            <Button size="small" onClick={onBack} className="mt-2">
              <Trans>Back to Missions</Trans>
            </Button>
          )}
        </div>
      )}

      {phase === 'expired' && (
        <div
          className="mx-auto flex w-full max-w-[600px] flex-col items-center gap-4 rounded-2xl border border-solid border-gray-100 px-10 py-12 text-center"
          style={{ animation: 'nectar-slide-up 0.4s ease-out' }}
        >
          <Text type="h5" className="text-gray-70">
            <Trans>Time&apos;s up</Trans>
          </Text>
          <Text type="body2-regular" className="text-gray-80">
            <Trans>Your assessment session has expired. You&apos;ll need to start a new one.</Trans>
          </Text>
          {!prolificMode && (
            <Button size="small" onClick={onBack} className="mt-2">
              <Trans>Back to Missions</Trans>
            </Button>
          )}
        </div>
      )}

      {phase === 'already-assessed' && (
        <div
          className="mx-auto flex w-full max-w-[600px] flex-col items-center gap-4 rounded-2xl border border-solid border-gray-100 px-10 py-12 text-center"
          style={{ animation: 'nectar-slide-up 0.4s ease-out' }}
        >
          <Text type="h5" className="text-gray-70">
            <Trans>Already Assessed</Trans>
          </Text>
          <Text type="body2-regular" className="text-gray-80">
            <Trans>
              You have already completed a proficiency assessment for this language. Retakes aren&apos;t allowed.
            </Trans>
          </Text>
          {!prolificMode && (
            <Button size="small" onClick={onBack} className="mt-2">
              <Trans>Back to Missions</Trans>
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

export { ProficiencyAssessmentFlow }
